import { List } from "immutable";
import { find } from "lodash";
// import { Stopwatch } from "performance-stopwatch";
import PubSub from "pubsub-js";

import { fileVersion } from "../../jsAppVersion";
import { ENTITY_LIST_PANEL } from "../constants/identifiers";
import PubSubTopics from "../constants/pubSubTopics";
import { activeTutorialPathSelectors } from "../modules/activeTutorialPath";
import { appStateActionTypes, appStateSelectors } from "../modules/appState";
import { attachmentSelectors } from "../modules/attachments";
import { definitionSelectors } from "../modules/definition";
import { typeSelectors } from "../modules/definition/types";
import { mindmapSelectors } from "../modules/mindmaps";
import { preferencesActionTypes } from "../modules/preferences";
import rootActionTypes from "../modules/rootActionTypes";
import { syncActionTypes } from "../modules/sync";
import syncSettingsSelectors from "../modules/sync/settings/selectors";
import { getActiveViewIsPrintable } from "../printing/utils/printable";
import { isNativeCommsInstalled, nativeCommsBroadcastMessageTypes } from ".";

const ignoreActionsForSaving = [rootActionTypes.LOAD_FILE, rootActionTypes.LOAD_SAMPLE, rootActionTypes.UPDATE_FILE];

const isSaveRequired = (newState, prevState, action) => {
  // Leaving this commented out code here in case I want to debug changes
  // again later to try to minimise them

  // const changeFound =
  //   ignoreActionsForSaving.indexOf(action.type) < 0 &&
  //   (newState.definitions !== prevState.definitions ||
  //   newState.data !== prevState.data ||
  //   newState.sync !== prevState.sync);

  // if (changeFound) {
  //   console.log(
  //     `isSaveRequired(): changeFound = ${changeFound}. definitions changed = ${
  //       newState.definitions !== prevState.definitions
  //     }, data changed = ${newState.data !== prevState.data}, sync changed = ${newState.sync !== prevState.sync}`
  //   );
  //   console.log(`Printing triggering action`);
  //   console.dirJs(action);

  //   if (newState.definitions !== prevState.definitions) {
  //     console.log(`Printing new definitions, old definitions`);
  //     console.log(JSON.stringify(newState.definitions.toJS()));
  //     console.log(JSON.stringify(prevState.definitions.toJS()));
  //   }
  //   if (newState.data !== prevState.data) {
  //     console.log(`Printing new data, old data`);
  //     console.log(JSON.stringify(newState.data.toJS()));
  //     console.log(JSON.stringify(prevState.data.toJS()));
  //   }
  //   if (newState.sync !== prevState.sync) {
  //     console.log(`Printing new sync, old sync`);
  //     console.log(JSON.stringify(newState.sync.toJS()));
  //     console.log(JSON.stringify(prevState.sync.toJS()));
  //   }
  // }

  // Special case: if loading a v2 file, then the changes must be pushed
  if (action.type === rootActionTypes.LOAD_FILE && action.convertedFromV2) {
    return true;
  }

  // If it is one of the action types we want to ignore, then we will not
  // trigger a save after this change, no matter what data has changed.
  // This is to avoid iCloud getting into a loop where a change on one computer
  // triggers a change on the other, and conflicts ensue.
  // Otherwise, we look to see if any relevant data has changed
  return (
    ignoreActionsForSaving.indexOf(action.type) < 0 &&
    (newState.definitions !== prevState.definitions ||
      newState.data !== prevState.data ||
      newState.sync !== prevState.sync)
  );
};

const getAttachmentsListIfChanged = (newState, prevState) => {
  const oldAttachments = attachmentSelectors.getEmbeddedAttachments(prevState);
  const newAttachments = attachmentSelectors.getEmbeddedAttachments(newState);
  if (oldAttachments === newAttachments) {
    return null;
  }
  return newAttachments;
};

/**
 * So far, do not save the selected items, markers, syncItems
 * @param {TimelineRecord} state
 */
const getAppStateToSave = state => {
  return state.appState.merge({
    selectedItems: List(),
    selectedMarkers: List(),
    selectedSyncItems: List()
  });
};

const getSyncToSave = state => {
  return state.sync.delete("externalRep").delete("status");
};

const getPrintOptionsToSave = state => {
  return state.printOptions;
};

const getSaveData = state => {
  const appStateToSave = getAppStateToSave(state);
  const syncToSave = getSyncToSave(state);
  return {
    definitions: state.definitions.toJS(),
    data: state.data.toJS(),
    sync: syncToSave.toJS(),
    appState: appStateToSave.toJS(),
    printOptions: getPrintOptionsToSave(state).toJS(),
    activeTutorialPath: activeTutorialPathSelectors.getActiveTutorialPath(state),
    fileVersion
  };
};

const getExternalSyncRepIfChanged = (newState, prevState, action) => {
  const externalSyncRepIgnoreActions = [
    rootActionTypes.LOAD_FILE,
    rootActionTypes.UPDATE_FILE,
    syncActionTypes.UPDATE_EXTERNAL_REP
  ];

  if (
    newState.sync.externalRep &&
    newState.sync.externalRep !== prevState.sync.externalRep &&
    externalSyncRepIgnoreActions.indexOf(action.type) < 0
  ) {
    return newState.sync.externalRep.toJS();
  }
  return null;
};

const getTypeList = state => {
  const definitionTypes = definitionSelectors.getEnabledDefinitions(state).types;
  return definitionTypes.allIds
    .map(id => {
      const t = definitionTypes.byId.get(id);
      return {
        id,
        label: t.label,
        showOnSidebar: t.showOnSidebar
      };
    })
    .toArray();
};

const getMindmapList = state => mindmapSelectors.getMindmapList(state).toJS();

const getAddMenuEnabledTypes = state => {
  const viewTypeIds = appStateSelectors.getTypeIdsForView(state, appStateSelectors.getActiveViewId(state));
  const sidebarIds = typeSelectors.getSidebarTypeIds(state);
  return viewTypeIds.push(sidebarIds).flatten();
};

const getAppStateForMenus = state => {
  const isEntityListVisible = appStateSelectors.getLeftPanelMode(state) === ENTITY_LIST_PANEL;
  const entityListType = isEntityListVisible ? appStateSelectors.getVisibleTypeListId(state) : null;
  const entityListSuperTypes = isEntityListVisible
    ? typeSelectors.getAllSuperTypesOfTypes(state, List([entityListType])).join(",")
    : null;
  // const entityListType = isEntityListVisible ? appStateSelectors.getVisibleTypeListId(state) : null;

  return {
    msg: nativeCommsBroadcastMessageTypes.APP_STATE_CHANGED,
    isInspectorOpen: appStateSelectors.isItemInspectorOpen(state),
    entityListType,
    entityListSuperTypes,
    selectedItemCount: appStateSelectors.getSelectedItemCount(state),
    selectedItemsShareParent: appStateSelectors.getSelectedItemsShareSuper(state),
    selectedItemsShareNarrativeParent: appStateSelectors.getSelectedItemsShareNarrativeParent(state),
    selectedItemsAreUnlocked: appStateSelectors.getSelectedItemsAreUnlocked(state),
    activeViewIsPrintable: getActiveViewIsPrintable(state),
    selectedMarkerCount: appStateSelectors.getSelectedMarkerCount(state),
    selectedSyncItemsCount: appStateSelectors.getSelectedSyncItemCount(state),
    isSyncOpen: appStateSelectors.isSyncTabOpen(state),
    isNarrativeDisabled: !definitionSelectors.isNarrativeAllowed(state),
    openViewCount: appStateSelectors.getOpenViewCount(state),
    activeViewType: appStateSelectors.getActiveViewType(state)
  };
};

const getPreferences = state => {
  // Just add the message to a plain-object version of our preferences
  return Object.assign(state.preferences.toJS(), {
    msg: nativeCommsBroadcastMessageTypes.PREFERENCES_CHANGED
  });
};

const changeViewTypeActions = [
  appStateActionTypes.SET_ACTIVE_VIEW,
  appStateActionTypes.SET_NEXT_ACTIVE_VIEW,
  appStateActionTypes.SWITCH_VIEW_TYPE
];

const getPushData = (nextState, initialState, action) => {
  const initialPresentState = initialState.present;
  const nextPresentState = nextState.present;

  const attachmentsList = getAttachmentsListIfChanged(nextPresentState, initialPresentState);
  const json = JSON.stringify(getSaveData(nextPresentState));
  let data = {
    msg: nativeCommsBroadcastMessageTypes.PUSH_DATA_TO_NATIVE,
    json,
    actionType: action.type,
    types: getTypeList(nextPresentState),
    mindmaps: getMindmapList(nextPresentState),
    enabledAddTypes: getAddMenuEnabledTypes(nextPresentState).toArray(),
    narrativeDisabled: definitionSelectors.isNarrativeAllowed(nextPresentState),
    storeRelativePaths: definitionSelectors.getStoreRelativePaths(nextPresentState),
    syncEnabled: false,
    fontPercentage: appStateSelectors.getFontPercentage(nextPresentState)
  };

  if (attachmentsList) {
    data.attachments = attachmentsList.toArray();
  }

  // #!if readonly !== true
// data.undoCount = nextState.past.size;
// data.redoCount = nextState.future.size;
// data.syncApplication = syncSettingsSelectors.getSyncApplication(nextPresentState);
// data.syncEnabled = syncSettingsSelectors.getSyncEnabled(nextPresentState);
// data.syncPath = syncSettingsSelectors.getSyncPath(nextPresentState);
// data.syncRootFolders = syncSettingsSelectors.getExternalSyncRootFolderIds(nextPresentState).join(",");
// 
  // If the external sync representation has changed, we should send that through also!
// const changedExternalRep = getExternalSyncRepIfChanged(nextPresentState, initialPresentState, action);
// if (changedExternalRep) {
// data.syncExternalRepresentation = JSON.stringify(changedExternalRep);
// }
// 
  // #!endif

  return data;
};

const isSyncConfigChanged = (newState, prevState) => {
  return (
    !prevState ||
    syncSettingsSelectors.getSyncApplication(newState) !== syncSettingsSelectors.getSyncApplication(prevState) ||
    syncSettingsSelectors.getSyncEnabled(newState) !== syncSettingsSelectors.getSyncEnabled(prevState) ||
    syncSettingsSelectors.getSyncPath(newState) !== syncSettingsSelectors.getSyncPath(prevState) ||
    syncSettingsSelectors.getExternalSyncRootFolderIds(newState) !==
      syncSettingsSelectors.getExternalSyncRootFolderIds(prevState)
  );
};

const getSyncConfigData = newState => {
  let data = {
    msg: nativeCommsBroadcastMessageTypes.PUSH_SYNC_CONFIG_TO_NATIVE,
    syncApplication: syncSettingsSelectors.getSyncApplication(newState),
    syncEnabled: syncSettingsSelectors.getSyncEnabled(newState),
    syncPath: syncSettingsSelectors.getSyncPath(newState),
    syncRootFolders: syncSettingsSelectors.getExternalSyncRootFolderIds(newState).join(",")
  };

  return data;
};

export default store => next => action => {
  // const sw = new Stopwatch();
  // sw.start(`pushStateToNative: start ${action.type}`);

  // If the Native Comms are not installed, then we don't need to do anything
  if (!isNativeCommsInstalled()) {
    // sw.lap(`pushStateToNative: early bail`);
    return next(action);
  }

  // Otherwise, send any changes through to Native Comms
  const initialState = store.getState();
  let result = next(action);

  // sw.lap(`pushStateToNative: run next`);

  const nextState = store.getState();

  const initialPresentState = initialState.present;
  const nextPresentState = nextState.present;

  const saveRequired = isSaveRequired(nextPresentState, initialPresentState, action);

  if (saveRequired) {
    const data = getPushData(nextState, initialState, action);
    PubSub.publish(PubSubTopics.sendNativeMessage, data);
  } else {
    if (isSyncConfigChanged(nextPresentState, initialPresentState)) {
      const data = getSyncConfigData(nextPresentState);
      PubSub.publish(PubSubTopics.sendNativeMessage, data);
    }

    if (changeViewTypeActions.indexOf(action.type) >= 0) {
      const data = {
        msg: nativeCommsBroadcastMessageTypes.UPDATE_NATIVE_MENUS,
        enabledAddTypes: getAddMenuEnabledTypes(nextPresentState).toArray()
      };
      PubSub.publish(PubSubTopics.sendNativeMessage, data);
    } else if (action.type === appStateActionTypes.SET_FONT_PERCENTAGE) {
      const data = {
        msg: nativeCommsBroadcastMessageTypes.UPDATE_FONT_PERCENTAGE,
        fontPercentage: appStateSelectors.getFontPercentage(nextPresentState)
      };
      PubSub.publish(PubSubTopics.sendNativeMessage, data);
    }
  }

  if (initialPresentState.appState !== nextPresentState.appState) {
    const data = getAppStateForMenus(nextPresentState);
    PubSub.publish(PubSubTopics.sendNativeMessage, data);
  }

  if (
    initialPresentState.preferences !== nextPresentState.preferences &&
    action.type !== preferencesActionTypes.LOAD_PREFERENCES &&
    find(preferencesActionTypes, v => v === action.type)
  ) {
    const data = getPreferences(nextPresentState);
    PubSub.publish(PubSubTopics.sendNativeMessage, data);
  }

  // sw.lap(`pushStateToNative: send data`);

  return result;
};
